import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Button, Colors, ControlledTextInput } from '@gripp/shared-ui';
import * as DocumentPicker from 'expo-document-picker';
import { formStyles } from 'libs/shared-ui/src/styles/forms';
import { useEffect, useMemo, useRef } from 'react';
import { CSVLink } from 'react-csv';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

interface Specification {
  key: string;
  value: string;
}

type SpecMap = Record<string, string>;

type ProductSpecificationsProps = {
  specifications: SpecMap | string | null | undefined;
  onSpecificationsChange: (specifications: SpecMap) => void;
};

export const ProductSpecifications = ({
  specifications,
  onSpecificationsChange,
}: ProductSpecificationsProps) => {
  const { t } = useTranslation();
  const csvRef = useRef<any>(null);

  const { control, watch } = useForm<{ rows: Specification[] }>({
    defaultValues: { rows: toRows(specifications) },
  });

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'rows',
  });

  // Sync parent prop changes into the form
  useEffect(() => {
    const nextRows = toRows(specifications);
    const incoming = rowsToObject(nextRows);
    const current = rowsToObject(watch('rows'));
    if (!shallowEqual(incoming, current)) {
      replace(nextRows);
    }
  }, [specifications]);

  // Notify parent on every change
  useEffect(() => {
    const sub = watch((values) => {
      const rows = (values.rows ?? []) as Specification[];
      onSpecificationsChange(rowsToObject(rows));
    });
    return () => sub.unsubscribe();
  }, [watch, onSpecificationsChange]);

  const addSpecification = () => {
    append({ key: '', value: '' });
  };

  const deleteSpecification = (index: number) => {
    remove(index);
  };

  const uploadData = async () => {
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: 'text/csv',
        copyToCacheDirectory: true,
      });

      if (result.canceled) {
        return;
      }

      const file = result.assets[0];
      
      // Web-compatible file reading
      let fileContent: string;
      if (file.uri.startsWith('file://')) {
        // For mobile, we can use fetch to read the file
        const response = await fetch(file.uri);
        fileContent = await response.text();
      } else {
        // For web, use FileReader
        const response = await fetch(file.uri);
        const blob = await response.blob();
        fileContent = await new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result as string);
          reader.readAsText(blob);
        });
      }
      
      // Simple CSV parsing: First two columns are key,value. Optional header row.
      const lines = fileContent.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      const csvRows: Specification[] = [];
      lines.forEach((line, idx) => {
        // Skip header if present
        if (idx === 0 && (/specification/i.test(line) || /key/i.test(line))) return;
        const parts = line.split(',');
        if (parts.length < 2) return;
        let key = parts[0].trim();
        let value = parts.slice(1).join(',').trim(); // allow commas in value (simple approach)
        key = stripQuotes(key);
        value = stripQuotes(value);
        if (key && value) csvRows.push({ key, value });
      });
      
      // Update specifications with CSV data
      replace(ensureRows(csvRows));
      Alert.alert(t('common.success'), t('product.specifications.upload_success'));
    } catch (error) {
      console.error('Upload error:', error);
      Alert.alert(t('common.error'), t('product.specifications.upload_error'));
    }
  };

  const downloadData = () => {
    const rows = watch('rows');
    // Check if there's data to download
    if (!rows || rows.length === 0 || !rows.some((spec: Specification) => spec.key.trim() && spec.value.trim())) {
      Alert.alert(t('common.info'), t('product.specifications.no_data_to_download'));
      return;
    }
    
    // Trigger the hidden CSVLink
    setTimeout(() => {
      csvRef.current?.link?.click();
    }, 50);
  };

  // Prepare CSV data for download
  const rows = watch('rows');
  const csvData = useMemo(() => {
    const valid = rows.filter((r) => r.key.trim() && r.value.trim());
    return valid.map((r) => ({ key: r.key.trim(), value: r.value.trim() }));
  }, [rows]);

  const csvHeaders = [
    { label: 'Specification', key: 'key' },
    { label: 'Value', key: 'value' },
  ];

  return (
    <View style={styles.card}>
      <View style={styles.cardHeader}>
        <Text style={formStyles.formItemTitle}>{t('product.specifications.title')}</Text>
        <View style={styles.headerActions}>
          <Button
            state='default'
            onPress={uploadData}
            variant='rectangular'
            leftIcon={<MaterialCommunityIcons name="upload" size={16} />}
          >
            {t('product.specifications.upload')}
          </Button>
          <Button
            state='default'
            onPress={downloadData}
            variant='rectangular'
            leftIcon={<MaterialCommunityIcons name="download" size={16} />}
          >
            {t('product.specifications.download')}
          </Button>
          {/* Hidden CSVLink for download */}
          <CSVLink
            ref={csvRef}
            style={{ display: 'none' }}
            data={csvData}
            headers={csvHeaders}
            filename={`specifications_${new Date().getTime()}.csv`}
          />
        </View>
      </View>
      <ScrollView style={styles.specificationsList}>
        {fields.map((field, index) => (
          <View key={field.id} style={styles.specificationRow}>
            <View style={styles.keyInputWrapper}>
              <ControlledTextInput
                control={control}
                name={`rows.${index}.key`}
                placeholder={t('product.specifications.label_placeholder')}
                outlinedTextInput
                height={44}
                disableInnerStyle
                hideBorder
              />
            </View>
            <View style={styles.valueInputWrapper}>
              <ControlledTextInput
                control={control}
                name={`rows.${index}.value`}
                placeholder={t('product.specifications.value_placeholder')}
                outlinedTextInput
                height={44}
                disableInnerStyle
                hideBorder
              />
            </View>
            <TouchableOpacity
              style={styles.deleteButton}
              onPress={() => deleteSpecification(index)}
            >
              <MaterialCommunityIcons name="delete-outline" size={20} color={Colors.role.fillDisabled} />
            </TouchableOpacity>
          </View>
        ))}
      </ScrollView>

      <Button
        state='default'
        onPress={addSpecification}
        variant='rectangular'
        size='large'
        leftIcon={<MaterialCommunityIcons name="plus" size={16} />}
      >
        {`${t('product.specifications.add_more')}`}
      </Button>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: Colors.role.surface,
    padding: 20,
    borderRadius: 8,
    gap: 20,
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardHeaderText: {
    fontSize: 20,
    fontWeight: 'bold',
    color: Colors.role.textStrong,
  },
  headerActions: {
    flexDirection: 'row',
    gap: 16,
  },
  headerAction: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  headerActionText: {
    fontSize: 14,
    color: Colors.primaryDark,
    fontWeight: '500',
  },
  specificationsList: {
    maxHeight: 300,
  },
  specificationRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 12,
  },
  specificationInput: {
    height: 44,
    backgroundColor: Colors.role.surface,
  },
  specificationKeyInput: {
    flex: 0.4,
  },
  specificationValueInput: {
    flex: 0.6,
  },
  keyInputWrapper: {
    flex: 0.4,
  },
  valueInputWrapper: {
    flex: 0.6,
  },
  deleteButton: {
    padding: 4,
  },
  addButton: {
    alignSelf: 'flex-start',
  },
  addButtonText: {
    fontSize: 14,
    color: Colors.primaryDark,
    fontWeight: '500',
  },
});

// Simple helpers
function toRows(specs: SpecMap | string | null | undefined): Specification[] {
  const base: Specification[] = [
    { key: '', value: '' },
    { key: '', value: '' },
    { key: '', value: '' },
  ];
  try {
    if (!specs) return base;
    let obj: any = specs;
    if (typeof specs === 'string') {
      if (!specs.trim()) return base;
      obj = JSON.parse(specs);
    }
    if (Array.isArray(obj)) {
      const arr = obj.map((it) => ({ key: String(it?.key ?? ''), value: String(it?.value ?? '') }));
      return ensureRows(arr);
    }
    if (typeof obj === 'object' && obj !== null) {
      const arr = Object.entries(obj).map(([k, v]) => ({ key: String(k), value: String(v) }));
      return ensureRows(arr);
    }
    return base;
  } catch {
    return base;
  }
}

function ensureRows(items: Specification[], min = 3): Specification[] {
  const out = [...items];
  while (out.length < min) out.push({ key: '', value: '' });
  return out;
}

function rowsToObject(items: Specification[]): SpecMap {
  const obj: SpecMap = {};
  items.forEach((it) => {
    const k = (it.key ?? '').trim();
    const v = (it.value ?? '').trim();
    if (k && v) obj[k] = v; // last one wins on duplicate keys
  });
  return obj;
}

function stripQuotes(s: string): string {
  if (s.startsWith('"') && s.endsWith('"')) return s.slice(1, -1);
  if (s.startsWith("'") && s.endsWith("'")) return s.slice(1, -1);
  return s;
}

function shallowEqual(a: Record<string, string>, b: Record<string, string>) {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  for (const k of aKeys) {
    if (a[k] !== b[k]) return false;
  }
  return true;
}
